import { Component, computed, effect, ElementRef, signal, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { AuthUserDto, ContractorScopesDto, TenantService } from '@artemis-software/wr-api';
import { CurrentUserService } from '@services/current-user.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NbToastrService } from '@nebular/theme';
import { currentTenant, isExternalTenant, isInternalTenant } from '@/utils/admin-utils';
import TenantTypeEnum = AuthUserDto.TenantTypeEnum;
import ContractorScopesEnum = ContractorScopesDto.ScopesEnum;
import { PendingChanges } from '@guards/pending-changes.guard';
import { asyncComputed } from '@/utils/signals/asyncComputed';

@Component({
  standalone: false,
  selector: 'wr-contractor-scope-configuration-page',
  templateUrl: './contractor-scope-configuration-page.component.html',
  styleUrls: ['./contractor-scope-configuration-page.component.scss'],
})
export class ContractorScopeConfigurationPageComponent implements PendingChanges {
  readonly pageSize = 10;

  @ViewChild('panelList') private panelListRef?: ElementRef<HTMLElement>;

  canEdit = computed(() => this.currentUserService.hasPrivilege('editContractorScopes'));
  form!: FormGroup;
  readonly formSignal = signal<FormGroup | undefined>(undefined);
  isEditing = false;
  saving$ = new BehaviorSubject(false);

  filterForm!: FormGroup;
  readonly currentTenant = currentTenant();
  readonly isInternalTenant = isInternalTenant();
  readonly isExternalTenant = isExternalTenant();
  readonly selectedExternalTenantId = signal<string | undefined>(undefined);
  readonly selectedContractorId = signal<string | undefined>(undefined);
  readonly contractorFilter = signal('');
  readonly visibleCount = signal(this.pageSize);

  readonly allContractorScopes = asyncComputed(() => {
    const selectedExternalTenantId = this.selectedExternalTenantId();
    if (selectedExternalTenantId) {
      return this.tenantService.findContractorScopes(selectedExternalTenantId);
    }
    return [];
  }, []);

  readonly filteredContractors = computed(() => {
    const scopes = this.allContractorScopes();
    const filter = this.contractorFilter().toLowerCase().trim();
    const filtered = filter ? scopes.filter(s => s.contractorName?.toLowerCase().includes(filter)) : scopes;
    return filtered.slice().sort((a, b) => (a.contractorName ?? '').localeCompare(b.contractorName ?? ''));
  });

  readonly visibleContractors = computed(() => this.filteredContractors().slice(0, this.visibleCount()));

  readonly selectedContractorControl = computed(() => {
    const form = this.formSignal();
    const selectedId = this.selectedContractorId();
    if (!form || !selectedId) return undefined;
    const controls = form.get('tenantControls') as FormArray;
    if (!controls) return undefined;
    return (controls.controls as FormGroup[]).find(c => c.get('id')?.value === selectedId) as FormGroup | undefined;
  });

  readonly selectedContractorName = computed(() => {
    const selectedId = this.selectedContractorId();
    return this.allContractorScopes().find(s => s.contractorId === selectedId)?.contractorName;
  });

  get contractorFilterValue(): string { return this.contractorFilter(); }
  set contractorFilterValue(v: string) {
    this.contractorFilter.set(v);
    this.visibleCount.set(this.pageSize);
    setTimeout(() => this.fillContainer(), 0);
  }

  onContainerScroll(): void {
    const container = this.panelListRef?.nativeElement;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop - clientHeight < 100 && this.visibleCount() < this.filteredContractors().length) {
      this.visibleCount.update(c => c + this.pageSize);
    }
  }

  private fillContainer(): void {
    const container = this.panelListRef?.nativeElement;
    if (!container || this.visibleCount() >= this.filteredContractors().length) return;
    if (container.scrollHeight <= container.clientHeight) {
      this.visibleCount.update(c => c + this.pageSize);
      setTimeout(() => this.fillContainer(), 0);
    }
  }

  constructor(
    private readonly tenantService: TenantService,
    private readonly toastrService: NbToastrService,
    private readonly formBuilder: FormBuilder,
    private readonly currentUserService: CurrentUserService,
  ) {
    effect(() => {
      const isExternalTenant = this.isExternalTenant();
      const currentTenant = this.currentTenant();
      if (isExternalTenant && currentTenant) {
        this.selectedExternalTenantId.set(currentTenant.id);
      }
    });

    this.filterForm = this.formBuilder.group({
      tenantId: this.formBuilder.control<string | undefined>(undefined),
    });

    this.filterForm.get('tenantId')?.valueChanges.subscribe(value => {
      this.isEditing = false;
      this.selectedContractorId.set(undefined);
      this.contractorFilter.set('');
      this.visibleCount.set(this.pageSize);
      this.selectedExternalTenantId.set(value);
    });

    effect(() => {
      const allContractorScopes = this.allContractorScopes();
      const tenantControls = allContractorScopes.map(contractorScopes => {
        return this.formBuilder.group({
          id: this.formBuilder.control(contractorScopes.contractorId),
          name: this.formBuilder.control(contractorScopes.contractorName),
          buildingBaseData: this.formBuilder.control(contractorScopes.scopes.includes(ContractorScopesEnum.BuildingBaseData)),
          buildingInspections: this.formBuilder.control(contractorScopes.scopes.includes(ContractorScopesEnum.BuildingInspections)),
          buildingTgas: this.formBuilder.control(contractorScopes.scopes.includes(ContractorScopesEnum.BuildingTgas)),
          buildingScans: this.formBuilder.control(contractorScopes.scopes.includes(ContractorScopesEnum.BuildingScans)),
          buildingSensors: this.formBuilder.control(contractorScopes.scopes.includes(ContractorScopesEnum.BuildingSensors)),
          buildingDocuments: this.formBuilder.control(contractorScopes.scopes.includes(ContractorScopesEnum.BuildingDocuments)),
          buildingEquipmentDocuments: this.formBuilder.control(contractorScopes.scopes.includes(ContractorScopesEnum.BuildingEquipmentDocuments)),
        });
      });
      const newForm = this.formBuilder.group({
        tenantControls: this.formBuilder.array(tenantControls),
      });
      newForm.disable();
      this.form = newForm;
      this.formSignal.set(newForm);
      setTimeout(() => this.fillContainer(), 0);
    });
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.isEditing;
  }

  get tenantControls(): FormArray {
    return this.form.get('tenantControls') as FormArray;
  }

  onEdit(): void {
    this.isEditing = true;
    this.form?.enable();
  }

  async submit(): Promise<void> {
    try {
      this.saving$.next(true);

      const contractorDtos: ContractorScopesDto[] = this.form.value.tenantControls.map((tenantControl: any) => {
        const scopes: ContractorScopesEnum[] = [];
        if (tenantControl.buildingBaseData) scopes.push(ContractorScopesEnum.BuildingBaseData);
        if (tenantControl.buildingInspections) scopes.push(ContractorScopesEnum.BuildingInspections);
        if (tenantControl.buildingTgas) scopes.push(ContractorScopesEnum.BuildingTgas);
        if (tenantControl.buildingScans) scopes.push(ContractorScopesEnum.BuildingScans);
        if (tenantControl.buildingSensors) scopes.push(ContractorScopesEnum.BuildingSensors);
        if (tenantControl.buildingDocuments) scopes.push(ContractorScopesEnum.BuildingDocuments);
        if (tenantControl.buildingEquipmentDocuments) scopes.push(ContractorScopesEnum.BuildingEquipmentDocuments);
        return {
          contractorId: tenantControl.id,
          contractorName: '',
          scopes,
        } as ContractorScopesDto;
      });

      const selectedExternalTenantId = this.selectedExternalTenantId();
      if (!selectedExternalTenantId) {
        this.toastrService.danger('Bitte wählen Sie einen Kunden aus.', 'Fehler');
        return;
      }
      await firstValueFrom(this.tenantService.mergeTenantScopes({ contractors: contractorDtos, externalTenantId: selectedExternalTenantId }));
      this.form.disable();
      this.isEditing = false;
      this.toastrService.success('Dienstleiter Zugriffe wurden erfolgreich gespeichert', 'Dienstleister Zugriffe');
    } catch (e) {
      console.error(e);
      this.toastrService.danger('Fehler beim Speichern der Dienstleiter Zugriffe.', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  cancel(): void {
    this.isEditing = false;
    this.form.disable();
  }

  protected readonly signal = signal;
  protected readonly TenantTypeEnum = TenantTypeEnum;
}
